import {
  DEFAULT_DEBOUNCE_MS,
  DEFAULT_DRAFT_EXPIRY_MS,
  DRAFT_KEY_PREFIX,
  type DraftEntry,
  type DraftKeyParams,
  type DraftMetadata,
  type DraftRegistryOptions,
  type SaveOptions,
  type StorageAdapter,
} from './types';

// ─── ID Generation ────────────────────────────────────────────────────────────

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return [Math.random().toString(36).slice(2, 10), Math.random().toString(36).slice(2, 10)].join(
    '-',
  );
}

// ─── Draft Key Builder ────────────────────────────────────────────────────────

/**
 * Build a namespaced draft key from structured params.
 *
 * Key format: `erp-draft:<workshopId>:<userId>:<module>:<entityType>:<scope>`
 *
 * where `<scope>` is either `edit:<entityId>` (editing an existing record)
 * or `new` (creating a new record).
 *
 * @example
 * buildDraftKey({ module: 'customers', entityType: 'customer', userId: 'u1', workshopId: 'ws1' })
 * // → 'erp-draft:ws1:u1:customers:customer:new'
 *
 * buildDraftKey({ module: 'customers', entityType: 'customer', entityId: '42', userId: 'u1', workshopId: 'ws1' })
 * // → 'erp-draft:ws1:u1:customers:customer:edit:42'
 */
export function buildDraftKey(params: DraftKeyParams): string {
  const parts = [
    DRAFT_KEY_PREFIX,
    params.workshopId ?? '_global',
    params.userId ?? '_anon',
    params.module,
    params.entityType,
    params.entityId ? `edit:${params.entityId}` : 'new',
  ];
  return parts.join(':');
}

// ─── Pending Debounce State ────────────────────────────────────────────────────

interface PendingState {
  timer: ReturnType<typeof setTimeout>;
  data: unknown;
  options: SaveOptions | undefined;
}

// ─── DraftRegistry ────────────────────────────────────────────────────────────

/**
 * Framework-agnostic draft (autosave) registry.
 *
 * Responsibilities:
 *  - Store and retrieve form state snapshots keyed by a structured draft key.
 *  - Debounce save operations so rapid keystrokes don't thrash storage.
 *  - Flush pending debounced saves on demand (e.g. before navigating away).
 *  - Prune expired drafts on startup and periodically.
 *  - Notify per-key React subscribers for `useSyncExternalStore` integration.
 *  - Maintain a per-key entry cache so `get()` returns stable references
 *    between saves (prevents unnecessary re-renders).
 *
 * React integration is in DraftManager.tsx and use-draft.ts.
 * This class has zero React imports and is testable in isolation.
 *
 * Typical lifecycle:
 *   1. Form mounts → `get(key)` to check for existing draft
 *   2. User edits → `saveDebounced(key, formValues)` on each change
 *   3. Successful submit → `remove(key)` to clear the draft
 *   4. User navigates away → draft survives until expiry
 *   5. Next time form opens → `get(key)` returns the draft; consumer restores
 */
export class DraftRegistry {
  private readonly _store: StorageAdapter;
  private readonly _debounceMs: number;
  private readonly _defaultExpiry: number;

  /** Pending debounced saves: key → { timer, data, options } */
  private readonly _pending = new Map<string, PendingState>();
  /** Per-key change listeners for useSyncExternalStore */
  private readonly _listeners = new Map<string, Set<() => void>>();
  /**
   * Snapshot cache for stable references.
   * Maps key → { entry, version } — returns same `entry` object reference
   * as long as version hasn't changed, preventing spurious re-renders.
   */
  private readonly _cache = new Map<string, { entry: DraftEntry<unknown>; version: number }>();

  constructor(options: DraftRegistryOptions) {
    this._store = options.store;
    this._debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
    this._defaultExpiry = options.defaultExpiryMs ?? DEFAULT_DRAFT_EXPIRY_MS;
  }

  // ─── Key Builder (static) ──────────────────────────────────────────────────

  static buildKey = buildDraftKey;

  // ─── Save ──────────────────────────────────────────────────────────────────

  /**
   * Immediately persist a draft snapshot for `key`.
   * Cancels any pending debounced save for the same key.
   */
  save<T>(key: string, data: T, options?: SaveOptions): void {
    // Cancel any pending debounced save for this key
    const pending = this._pending.get(key);
    if (pending) {
      clearTimeout(pending.timer);
      this._pending.delete(key);
    }

    this._doSave(key, data, options);
  }

  /**
   * Schedule a debounced save for `key`.
   * If called again before the debounce delay elapses, the previous save is
   * cancelled and the timer resets — standard debounce behaviour.
   * The latest `data` is always what gets saved.
   *
   * Call `flush(key)` to immediately save any pending debounced data.
   */
  saveDebounced<T>(key: string, data: T, options?: SaveOptions): void {
    const existing = this._pending.get(key);
    if (existing) clearTimeout(existing.timer);

    const timer = setTimeout(() => {
      this._pending.delete(key);
      this._doSave(key, data, options);
    }, this._debounceMs);

    this._pending.set(key, { timer, data, options });
  }

  /**
   * Flush a pending debounced save for `key` immediately.
   * No-op if there is no pending save for that key.
   *
   * Call before navigation away or form unmount to ensure the latest data
   * is persisted before the timer fires.
   */
  flush(key: string): void {
    const pending = this._pending.get(key);
    if (!pending) return;

    clearTimeout(pending.timer);
    this._pending.delete(key);
    this._doSave(key, pending.data, pending.options);
  }

  /**
   * Flush ALL pending debounced saves.
   * Call from ERPCoreProvider on route change or app unload.
   */
  flushAll(): void {
    for (const key of [...this._pending.keys()]) {
      this.flush(key);
    }
  }

  // ─── Retrieve ──────────────────────────────────────────────────────────────

  /**
   * Retrieve a draft entry by key.
   *
   * Returns a **stable reference** as long as the `version` hasn't changed —
   * critical for `useSyncExternalStore` snapshot stability. Two consecutive
   * `get()` calls without an intervening `save()` return the same object.
   *
   * Returns `null` if no draft exists OR if the draft has expired.
   */
  get<T>(key: string): DraftEntry<T> | null {
    const stored = this._store.get<T>(key);
    if (!stored) {
      this._cache.delete(key);
      return null;
    }

    // Treat expired entries as absent
    if (stored.meta.expiresAt <= Date.now()) {
      this._store.remove(key);
      this._cache.delete(key);
      return null;
    }

    // Return cached reference if version matches (same data)
    const cached = this._cache.get(key);
    if (cached?.version === stored.meta.version) {
      return cached.entry as DraftEntry<T>;
    }

    // New or updated entry — update cache
    this._cache.set(key, { entry: stored, version: stored.meta.version });
    return stored;
  }

  // ─── Remove ────────────────────────────────────────────────────────────────

  /**
   * Remove a draft and cancel any pending debounced save.
   *
   * Call after a successful form submission to clear the preserved state.
   * Idempotent — no-op if the draft does not exist.
   */
  remove(key: string): void {
    // Cancel pending debounced save so stale data doesn't overwrite nothing
    const pending = this._pending.get(key);
    if (pending) {
      clearTimeout(pending.timer);
      this._pending.delete(key);
    }

    this._store.remove(key);
    this._cache.delete(key);
    this._notifyKey(key);
  }

  /**
   * Remove all drafts matching the given prefix.
   *
   * Useful for clearing a user's or workshop's drafts on logout.
   *
   * @param prefix  e.g. `buildDraftKey({ workshopId: 'ws-1', module: 'customers', ... })`
   *                or just `'erp-draft:ws-1'` to clear all drafts for workshop ws-1.
   */
  clear(prefix?: string): void {
    // Flush and cancel any pending saves matching the prefix
    for (const key of [...this._pending.keys()]) {
      if (!prefix || key.startsWith(prefix)) {
        const pending = this._pending.get(key);
        if (!pending) continue;
        clearTimeout(pending.timer);
        this._pending.delete(key);
      }
    }

    this._store.clear(prefix);

    // Invalidate cache for affected keys
    for (const key of [...this._cache.keys()]) {
      if (!prefix || key.startsWith(prefix)) {
        this._cache.delete(key);
        this._notifyKey(key);
      }
    }
  }

  // ─── Queries ───────────────────────────────────────────────────────────────

  /** Returns true if a non-expired draft exists for `key`. */
  exists(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Returns the metadata for an existing draft, or null.
   * Useful for showing "Last saved X minutes ago" without fetching full data.
   */
  getMetadata(key: string): DraftMetadata | null {
    return this.get(key)?.meta ?? null;
  }

  // ─── Expiry ────────────────────────────────────────────────────────────────

  /**
   * Scan all stored drafts and remove any that have expired.
   * Called automatically on provider mount and every `PRUNE_INTERVAL_MS`.
   */
  pruneExpired(): void {
    const now = Date.now();
    const allKeys = this._store.keys();

    for (const key of allKeys) {
      const entry = this._store.get(key);
      if (entry && entry.meta.expiresAt <= now) {
        this._store.remove(key);
        this._cache.delete(key);
        this._notifyKey(key);
      }
    }
  }

  // ─── Subscriptions ─────────────────────────────────────────────────────────

  /**
   * Subscribe to changes for a specific draft key.
   * Fires whenever the draft at `key` is saved, removed, or pruned.
   *
   * Returns an unsubscribe function. Used by `useDraft()` / `useSyncExternalStore`.
   */
  subscribe(key: string, listener: () => void): () => void {
    let listeners = this._listeners.get(key);
    if (!listeners) {
      listeners = new Set();
      this._listeners.set(key, listeners);
    }
    listeners.add(listener);

    return () => {
      const set = this._listeners.get(key);
      if (set) {
        set.delete(listener);
        if (set.size === 0) this._listeners.delete(key);
      }
    };
  }

  // ─── Debug ─────────────────────────────────────────────────────────────────

  debug(): void {
    const allKeys = this._store.keys();
    console.warn('[DraftRegistry] stored keys:', allKeys);
    console.warn('[DraftRegistry] pending saves:', [...this._pending.keys()]);
  }

  // ─── Private ───────────────────────────────────────────────────────────────

  private _doSave<T>(key: string, data: T, options?: SaveOptions): void {
    const existing = this._store.get<T>(key);
    const now = Date.now();

    const meta: DraftMetadata = {
      savedAt: now,
      expiresAt: now + (options?.expiryMs ?? this._defaultExpiry),
      version: (existing?.meta.version ?? 0) + 1,
      sessionId: existing?.meta.sessionId ?? generateSessionId(),
    };

    const entry: DraftEntry<T> = { data, meta };
    this._store.set(key, entry);
    this._cache.delete(key); // Invalidate cache so next get() fetches fresh
    this._notifyKey(key);
  }

  private _notifyKey(key: string): void {
    const listeners = this._listeners.get(key);
    if (listeners) {
      for (const listener of listeners) {
        listener();
      }
    }
  }
}
